"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PHI_ROLES = exports.roleLabel = exports.isRoleName = exports.PRACTICE_ROLES = exports.DEFAULT_ROLE = exports.ROLE_NAMES = exports.roles = exports.ac = exports.ACO_RESOURCES = exports.statement = void 0;
// ONE source of truth for roles and permissions (T04 §9.1, T11). Imported by the server guards
// (apps/server/src/plugins/auth.ts) and the web <Can> gate (apps/web/src/features/auth/can.tsx).
const access_1 = require("better-auth/plugins/access");
const access_2 = require("better-auth/plugins/admin/access");
/**
 * Resources and the actions allowed on them. Add a resource here first; TypeScript then
 * forces every role and every check to use only these names.
 */
exports.statement = {
    ...access_2.defaultStatements, // user: [...], session: [...] from the admin plugin
    patient: ["read", "create", "update", "delete", "export"],
    chart: ["read", "create", "update", "sign"],
    audit: ["read"],
    settings: ["read", "update"],
    /** B2 demo resource: GET /api/admin/ping requires admin:["ping"]. */
    admin: ["ping"],
    // ACO Quality Analytics (SPEC §15.3, D-977; `acoData` without `registerNpis`, D-1012).
    acoQuality: ["read"],
    acoPatients: ["read", "export"],
    acoData: ["load", "calculate"],
    acoSettings: ["readCore", "readAll", "update"],
    acoDemo: ["reset"],
    acoAdmin: ["manageAccess", "readAudit"],
    // CR-001 §5.3 (CRQ-027, D-1017).
    acoPractice: ["read"],
    acoGaps: ["read", "close", "assign", "export", "import"],
    acoClosures: ["review", "export"],
    acoEngine: ["read", "run"],
    acoMatch: ["review"],
    acoShared: ["read"],
    // J-320 (Louis, 2026-10-23): Gap Workbook Uploads. An uploader uploads, lists and deletes only their own
    // uploads; the admin lists every upload and downloads any, and neither uploads nor deletes.
    acoGapUploads: ["upload", "readOwn", "deleteOwn", "readAll", "download"],
};
/** The ACO module's statements, in declaration order. `GET /api/v1/me/access` reports these. */
exports.ACO_RESOURCES = [
    "acoQuality",
    "acoPatients",
    "acoData",
    "acoSettings",
    "acoDemo",
    "acoAdmin",
    "acoPractice",
    "acoGaps",
    "acoClosures",
    "acoEngine",
    "acoMatch",
    "acoShared",
    "acoGapUploads",
];
// Explicit alias types keep the generic instantiation in the emitted .d.ts so `admin({ ac, roles })`
// in the server and `adminClient({ ac, roles })` in the web type-check against the compiled package.
exports.ac = (0, access_1.createAccessControl)(exports.statement);
/**
 * The admin row of SPEC §15.3 and CR-001 §5.3: every action of every ACO statement but J-320's upload,
 * readOwn and deleteOwn (Louis, 2026-10-23: the admin "cannot delete. can only download").
 */
const ACO_ALL = {
    acoQuality: ["read"],
    acoPatients: ["read", "export"],
    acoData: ["load", "calculate"],
    acoSettings: ["readCore", "readAll", "update"],
    acoDemo: ["reset"],
    acoAdmin: ["manageAccess", "readAudit"],
    acoPractice: ["read"],
    acoGaps: ["read", "close", "assign", "export", "import"],
    acoClosures: ["review", "export"],
    acoEngine: ["read", "run"],
    acoMatch: ["review"],
    acoShared: ["read"],
    acoGapUploads: ["readAll", "download"],
};
/** J-320: an uploader's actions, for the roles that download the Gap Closure Workbook (`acoGaps.export`). */
const GAP_UPLOADER = ["upload", "readOwn", "deleteOwn"];
/**
 * The seeded framework roles (admin@ / clinician@ / viewer@vbltc.test, T05 §9.9) plus the ACO
 * module's `analyst` (D-977) and CR-001's `practice_manager` and `practice_staff` (D-1017). The ACO
 * rows are SPEC §15.3 and the CR-001 §5.3 matrix; `permissions.test.ts` asserts every cell.
 */
exports.roles = {
    admin: exports.ac.newRole({
        ...access_2.adminAc.statements, // manage users, sessions, bans, impersonation
        patient: ["read", "create", "update", "delete", "export"],
        chart: ["read", "create", "update", "sign"],
        audit: ["read"],
        settings: ["read", "update"],
        admin: ["ping"],
        ...ACO_ALL,
    }),
    // Aggregate and patient data, the core settings tabs read-only, and CR-001's review, export,
    // engine, match and shared-patient screens; never closes a gap (CR-001 §5.3). Loading and
    // Calculate are admin-only (CR-004, D-1220).
    analyst: exports.ac.newRole({
        acoQuality: ["read"],
        acoPatients: ["read", "export"],
        acoSettings: ["readCore"],
        acoPractice: ["read"],
        // CR-002 CR2-072 (owner's answer B14): the workbook for everyone who can see the lists.
        acoGaps: ["read", "export"],
        acoClosures: ["review", "export"],
        acoEngine: ["read", "run"],
        acoMatch: ["review"],
        acoShared: ["read"],
        acoGapUploads: GAP_UPLOADER,
    }),
    // No ACO module permission (SPEC §15.3, CRQ-020): denied and audited by `requirePermission`.
    clinician: exports.ac.newRole({
        patient: ["read", "create", "update"],
        chart: ["read", "create", "update", "sign"],
    }),
    // Default role for accounts an admin creates: read-only, no PHI export, no admin (SPEC §15.3).
    viewer: exports.ac.newRole({
        patient: ["read"],
        chart: ["read"],
        acoQuality: ["read"],
        acoPractice: ["read"],
    }),
    // Practice roles see raw rates for their granted (ACO, TIN) pairs only, never deciles or points
    // (CRQ-023), so they hold no `acoQuality`.
    practice_manager: exports.ac.newRole({
        acoPractice: ["read"],
        acoGaps: ["read", "close", "assign", "export", "import"],
        acoGapUploads: GAP_UPLOADER,
    }),
    practice_staff: exports.ac.newRole({
        acoPractice: ["read"],
        acoGaps: ["read", "close", "export"],
        acoGapUploads: GAP_UPLOADER,
    }),
};
exports.ROLE_NAMES = Object.keys(exports.roles);
exports.DEFAULT_ROLE = "viewer";
/**
 * Roles scoped by practice grants, (ACO, TIN) pairs in `core.user_practice_access`, instead of an
 * ACO set (CRQ-022, D-1017). `aco-scope.ts` in the server reads this list.
 */
exports.PRACTICE_ROLES = ["practice_manager", "practice_staff"];
// Object.hasOwn, not `in`: `in` walks the prototype chain, so `"toString" in roles` is true and a
// user whose role column held "toString" or "constructor" passed this guard. `roles[that]` then
// returned an Object.prototype method with no .authorize, and requirePermission threw a TypeError
// (HTTP 500) instead of denying with 403. (D-037)
const isRoleName = (value) => typeof value === "string" && Object.hasOwn(exports.roles, value);
exports.isRoleName = isRoleName;
/** Each role in words, as the top bar's user menu names it (J-280, QB3 item 6c). */
const ROLE_LABELS = {
    admin: "Admin",
    analyst: "Analyst",
    clinician: "Clinician",
    viewer: "Viewer",
    practice_manager: "Practice manager",
    practice_staff: "Practice staff",
};
/** The role in words; an unknown role is shown as stored, and none as "No role". */
const roleLabel = (value) => (0, exports.isRoleName)(value) ? ROLE_LABELS[value] : value || "No role";
exports.roleLabel = roleLabel;
/**
 * Roles whose users may read PHI; the MFA gate applies to them (T12 §9.1 #9). Every role is listed
 * (CR-001 §5.3 rule, D-1017): the per-user PHI flag in `core.user_access_profile` decides patient
 * detail, and the MFA gate covers every account that could hold it.
 */
exports.PHI_ROLES = [
    "admin",
    "analyst",
    "clinician",
    "viewer",
    "practice_manager",
    "practice_staff",
];
//# sourceMappingURL=permissions.js.map